import * as THREE from 'three';
import '../ui/fortress.css';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { DungeonEnemy } from '../entities/dungeonEnemy.js';
import { HUD } from '../ui/hud.js';
import { getStat, trainStat, addCrumbs, setFlag } from '../core/save.js';
import { addFound, hasFound } from '../core/collection.js';
import { addIngredient } from '../core/ingredients.js';

// The Fortress Crypt — a harder dungeon level beneath the keep.
// Skeleton bread warriors patrol cold stone corridors, and a dusty chest waits
// beyond them.

export function createFortressCryptScene(){
  return () => new FortressCryptScene();
}

const STONE   = 0x6E6A62;
const STONE_D = 0x4E4A44;
const MORTAR  = 0x36332E;
const WOOD    = 0x5A3A22;
const GOLD    = 0xE8C24B;

class FortressCryptScene {
  constructor(){
    this.state = 'explore';
    this.panels = [];
    this.vignette = null;
    this.combatToastT = 0;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x140E14);
    this.scene.fog = new THREE.Fog(0x140E14, 12, 42);

    this.camera = new THREE.PerspectiveCamera(52, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 13;
    this.camTarget = new THREE.Vector3(0, 1.6, 4);
    this.azimuth = 0; this.polar = 0.5;

    this._buildLights();
    this._buildCrypt();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 8);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.enemies = [
      new DungeonEnemy('skeleton'),
      new DungeonEnemy('skeleton'),
      new DungeonEnemy('skeleton'),
      new DungeonEnemy('skeleton'),
      new DungeonEnemy('golem')
    ];
    this.enemies[0].root.position.set(-2.2, 0, 0);
    this.enemies[1].root.position.set( 2.2, 0, -0.8);
    this.enemies[2].root.position.set(-1.8, 0, -8);
    this.enemies[3].root.position.set( 1.8, 0, -8.8);
    this.enemies[4].root.position.set( 0,   0, -16);
    this.enemies.forEach(e => this.scene.add(e.root));

    this.traps = [
      { pos: new THREE.Vector3(0, 0, -4.5), phase: 0, active: false },
      { pos: new THREE.Vector3(0, 0, -12.5), phase: 2, active: false }
    ];
    this._buildTraps();

    this.chestOpen = false;
    this.playerMaxHp = 5;
    this.playerHp = this.playerMaxHp;
    this.swingCooldown = 0;
    this.invulnT = 0;

    this.hotspots = [
      { pos: new THREE.Vector3(0, 0, -18.5), r: 1.8, label: '📦 open the crypt chest', action: () => this._openChest() },
      { pos: new THREE.Vector3(0, 0, 9.5), r: 2.0, label: '🚪 exit to the Fortress', action: () => this._exit() }
    ];
    this.nearSpot = null;
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0x3A303A, 0.65));
    const moon = new THREE.DirectionalLight(0x5A6A90, 0.5);
    moon.position.set(4, 14, 8); moon.castShadow = true;
    moon.shadow.mapSize.set(2048, 2048);
    const s = 18;
    moon.shadow.camera.left = -s; moon.shadow.camera.right = s;
    moon.shadow.camera.top = s; moon.shadow.camera.bottom = -s;
    this.scene.add(moon);
  }

  _buildCrypt(){
    const HALF_W = 6, Z0 = -22, Z1 = 11;
    this.roomBounds = { halfW: HALF_W, z0: Z0, z1: Z1 };
    const len = Z1 - Z0, midZ = (Z0 + Z1) / 2;

    // floor
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(HALF_W * 2, len), new THREE.MeshLambertMaterial({ color: STONE }));
    floor.rotation.x = -Math.PI/2; floor.position.set(0, 0, midZ); floor.receiveShadow = true;
    this.scene.add(floor);

    // walls
    const wallMat = new THREE.MeshLambertMaterial({ color: STONE_D });
    const wallGeo = new THREE.PlaneGeometry(len, 8);
    const wallL = new THREE.Mesh(wallGeo, wallMat);
    wallL.rotation.y = Math.PI/2; wallL.position.set(-HALF_W, 4, midZ); wallL.receiveShadow = true;
    this.scene.add(wallL);
    const wallR = new THREE.Mesh(wallGeo, wallMat);
    wallR.rotation.y = -Math.PI/2; wallR.position.set(HALF_W, 4, midZ); wallR.receiveShadow = true;
    this.scene.add(wallR);

    // pillars
    for (let z = -2; z >= -18; z -= 4){
      [-4.5, 4.5].forEach(x => {
        cyl(0.35, 0.4, 7.0, STONE, x, 3.5, z, this.scene, 14);
      });
    }

    // back wall
    box(HALF_W * 2, 8, 0.4, STONE_D, 0, 4, Z0, this.scene);
    // entrance wall
    box(HALF_W * 2, 8, 0.4, STONE_D, 0, 4, Z1, this.scene);
    const archW = 2.6;
    box((HALF_W * 2 - archW) / 2, 8, 0.5, STONE_D, -(archW/2 + (HALF_W * 2 - archW)/4), 4, Z1, this.scene);
    box((HALF_W * 2 - archW) / 2, 8, 0.5, STONE_D,  (archW/2 + (HALF_W * 2 - archW)/4), 4, Z1, this.scene);
    box(archW + 0.2, 2.4, 0.5, STONE, 0, 6.6, Z1, this.scene);

    // ceiling
    const ceil = new THREE.Mesh(new THREE.PlaneGeometry(HALF_W * 2, len), new THREE.MeshLambertMaterial({ color: 0x140E14 }));
    ceil.rotation.x = Math.PI/2; ceil.position.set(0, 8, midZ); this.scene.add(ceil);

    // sconces
    const sconces = [ {x:-5.5,z:4}, {x:5.5,z:4}, {x:-5.5,z:-10}, {x:5.5,z:-10}, {x:-5.5,z:-19}, {x:5.5,z:-19} ];
    this.sconces = [];
    sconces.forEach(s => {
      const g = new THREE.Group(); g.position.set(s.x, 3.4, s.z); this.scene.add(g);
      cyl(0.04, 0.04, 0.4, 0x5A3A22, s.x < 0 ? 0.18 : -0.18, 0, 0, g, 6);
      const flame = new THREE.Group(); flame.position.set(s.x < 0 ? 0.42 : -0.42, 0.28, 0); g.add(flame);
      const c1 = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.42, 8), new THREE.MeshBasicMaterial({ color: 0xFF9A3C }));
      c1.position.y = 0.18; flame.add(c1);
      const light = new THREE.PointLight(0xFF9A3C, 0.6, 8, 2);
      light.position.set(s.x < 0 ? s.x + 0.5 : s.x - 0.5, 3.8, s.z); this.scene.add(light);
      this.sconces.push({ flame, light, phase: Math.random() * 6 });
    });

    // chest
    this.chestGroup = new THREE.Group(); this.chestGroup.position.set(0, 0, -18.5); this.scene.add(this.chestGroup);
    box(1.8, 0.9, 1.0, WOOD, 0, 0.45, 0, this.chestGroup);
    box(1.9, 0.12, 1.1, GOLD, 0, 0.45, 0, this.chestGroup);
    box(0.14, 0.9, 1.1, GOLD, -0.8, 0.45, 0, this.chestGroup);
    box(0.14, 0.9, 1.1, GOLD, 0.8, 0.45, 0, this.chestGroup);
    this.chestLock = box(0.24, 0.28, 0.1, GOLD, 0, 0.5, 0.56, this.chestGroup);
  }

  _buildTraps(){
    this.trapMeshes = [];
    this.traps.forEach(t => {
      const g = new THREE.Group(); g.position.copy(t.pos); this.scene.add(g);
      // pit rim
      const rim = new THREE.Mesh(new THREE.RingGeometry(0.9, 1.3, 24), new THREE.MeshLambertMaterial({ color: 0x2A202A }));
      rim.rotation.x = -Math.PI/2; rim.position.y = 0.04; g.add(rim);
      // spike plate
      const plate = new THREE.Mesh(new THREE.CircleGeometry(0.85, 20), new THREE.MeshLambertMaterial({ color: 0x3A303A }));
      plate.rotation.x = -Math.PI/2; plate.position.y = 0.05; g.add(plate);
      // spikes (start hidden below)
      const spikes = new THREE.Group(); spikes.position.y = -0.4; g.add(spikes);
      for (let i = 0; i < 7; i++){
        const a = (i / 7) * Math.PI * 2;
        const sp = cyl(0.05, 0.02, 0.6, 0x888888, Math.cos(a)*0.35, 0.3, Math.sin(a)*0.35, spikes, 6);
        sp.rotation.x = Math.PI;
      }
      this.trapMeshes.push({ group: g, spikes, t });
    });
  }

  // ---- lifecycle ----
  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.28, 0.62];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    this.vignette = document.createElement('div');
    this.vignette.className = 'fortress-vignette';
    document.body.appendChild(this.vignette);
    this._positionCamera();
    this.bread.setLantern(hasFound('ghost-lamp') ? 1.1 : 0);
    this._toExplore(true);
    this._buildCombatHud();
    this._updateHpBar();
  }

  exit(){
    this._clearPanels();
    if (this.vignette){ this.vignette.remove(); this.vignette = null; }
    if (this.hudEl){ this.hudEl.remove(); this.hudEl = null; }
    this.input.onTap = null;
    HUD.showEnter(null); HUD.say('');
  }

  _positionCamera(){
    this.azimuth = this.input?.azimuthGoal ?? 0;
    this.polar   = this.input?.polarGoal ?? 0.5;
    const b = this.roomBounds;
    this.camTarget.set(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5 + 1);
    this.camTarget.z = Math.max(b.z0 + 2, Math.min(b.z1 - 2, this.camTarget.z));
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this._clampCamera();
    this.camera.lookAt(this.camTarget);
  }

  _clampCamera(){
    const b = this.roomBounds;
    const pad = 1.5;
    this.camera.position.x = Math.max(-b.halfW + pad, Math.min(b.halfW - pad, this.camera.position.x));
    this.camera.position.z = Math.max(b.z0 + pad, Math.min(b.z1 - pad, this.camera.position.z));
    this.camera.position.y = Math.max(2, Math.min(9, this.camera.position.y));
  }

  _toExplore(first){
    this.state = 'explore';
    this._clearPanels();
    this.bread.resetLimbs();
    this.input.onTap = (ndc, ray) => this._tap(ray);
    HUD.setSubtitle('fortress crypt');
    if (first) HUD.say('The crypt reeks of old dough and dust. Skeleton shapes clatter in the dark.');
    HUD.setHint('tap the ground to walk · tap skeletons during their flash to strike · watch the spike traps');
    HUD.hideHintSoon(9000);
  }

  _buildCombatHud(){
    this.hudEl = document.createElement('div');
    this.hudEl.className = 'dungeon-hud';
    this.hudEl.innerHTML = `<div class="dungeon-hp"><span id="crypt-hp-bar" class="ok" style="width:100%"></span></div>`;
    document.body.appendChild(this.hudEl);
    this.hpBar = this.hudEl.querySelector('#crypt-hp-bar');
  }

  _updateHpBar(){
    if (!this.hpBar) return;
    const pct = Math.max(0, (this.playerHp / this.playerMaxHp) * 100);
    this.hpBar.style.width = pct + '%';
    this.hpBar.className = pct > 60 ? 'ok' : pct > 30 ? 'warn' : 'crit';
  }

  _combatToast(text){
    this.combatToastT = 1.2;
    if (!this.toastEl){
      this.toastEl = document.createElement('div');
      this.toastEl.className = 'dungeon-toast';
      document.body.appendChild(this.toastEl);
    }
    this.toastEl.textContent = text;
    this.toastEl.style.opacity = '1';
  }

  // ---- interactions ----
  _tap(ray){
    if (this.state !== 'explore') return;
    if (this.swingCooldown <= 0 && this._checkEnemyClick(ray)) return;

    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-5, Math.min(5, gp.x));
    gp.z = Math.max(-21, Math.min(10, gp.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  _checkEnemyClick(ray){
    const targets = this.enemies.filter(e => !e.dead).map(e => e.hitTarget);
    if (!targets.length) return false;
    const hits = ray.intersectObjects(targets, false);
    if (hits.length){
      const enemy = hits[0].object.userData.enemy;
      if (enemy) this._swingAt(enemy);
      return true;
    }
    return false;
  }

  _swingAt(enemy){
    this.bread.stop();
    this.bread.startPose('strike', 0.35);
    this.bread.drawFace('effort');
    this.swingCooldown = 0.45;
    const dmg = 1 + Math.floor(getStat('str') * 0.5);
    const killed = enemy.takeDamage(dmg);
    this._combatToast(killed ? 'crunched!' : `hit -${dmg}`);
    if (killed){
      addCrumbs(4); HUD.refreshCrumbs();
      addIngredient('bone-meal', 1);
    }
  }

  _openChest(){
    if (this.chestOpen) return;
    this.chestOpen = true;
    this.chestLock.visible = false;
    this.bread.drawFace('yum');
    const CRUMBS = 100;
    addCrumbs(CRUMBS); HUD.refreshCrumbs();
    const first = !hasFound('relic-bone');
    addFound('relic-bone');
    setFlag('fortressCryptCleared', true);
    const strGain = trainStat('str', 1);
    if (strGain > 0) HUD.refreshStats();

    this._clearPanels();
    const p = this._panel(`
      <div class="cafe-card">
        <div class="cafe-grade">🦴</div>
        <h2>Crypt Hoard</h2>
        <div class="cafe-sub">the skeletons guarded this well</div>
        <p class="fortress-loot">🍞 Baker King's Bread-Bone</p>
        <p class="fortress-note">${first ? 'an ancient fossilized loaf — added to your collection!' : 'another bread-bone. still crunchy.'}</p>
        <p class="cafe-payout">🍞 +${CRUMBS} crumbs${strGain > 0 ? ` · +${strGain} STR` : ''}</p>
        <button class="cafe-btn ghost close">huzzah!</button>
      </div>
    `);
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _exit(){
    this.manager.goTo('fortress', { from: 'fortressCrypt' });
  }

  _defeat(){
    this.state = 'defeat';
    this.input.onTap = null;
    HUD.say('The crypt overwhelms you… you retreat to the Fortress hall.');
    setTimeout(() => this.manager.goTo('fortress', { from: 'fortressCrypt', defeated: true }), 2200);
  }

  // ---- panels ----
  _panel(html, bare){
    const el = document.createElement('div');
    el.className = bare ? '' : 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  // ---- loop ----
  update(dt){
    const t = performance.now() * 0.001;

    // sconce flicker
    this.sconces.forEach((s, i) => {
      s.light.intensity = 0.5 + 0.18 * Math.sin(t * 15 + s.phase) + 0.08 * Math.sin(t * 31 + i);
      s.flame.scale.y = 0.9 + 0.18 * Math.sin(t * 20 + s.phase);
    });

    if (this.swingCooldown > 0) this.swingCooldown -= dt;

    const playerPos = this.bread.root.position;
    this.invulnT = Math.max(0, this.invulnT - dt);

    // traps
    this.trapMeshes.forEach(tm => {
      const period = 2.5;
      const cycle = (t + tm.t.phase) % period;
      const active = cycle > 1.6 && cycle < 2.2;
      tm.t.active = active;
      const up = active ? 0.45 : 0;
      tm.spikes.position.y += (up - tm.spikes.position.y) * Math.min(1, dt * 12);
      if (active && tm.spikes.position.y > 0.3 && playerPos.distanceTo(tm.t.pos) < 1.1 && this.invulnT <= 0){
        this.playerHp--;
        this.invulnT = 0.8;
        this._updateHpBar();
        this._combatToast('spiked! -1 HP');
        this.bread.drawFace('o');
        if (this.playerHp <= 0) this._defeat();
      }
    });

    // enemies
    this.enemies.forEach(e => {
      if (e.dead) return;
      const d = e.root.position.distanceTo(playerPos);
      if (e.state === 'idle' && d < 5.5) e.startAttack();
      e.update(dt, playerPos);
      if (e.state === 'attacking' && !e.dealtDamage && d < 1.5){
        e.dealtDamage = true;
        const dodgeChance = Math.min(0.4, getStat('agi') * 0.07);
        if (Math.random() < dodgeChance){
          this._combatToast('dodged!');
        } else if (this.invulnT <= 0){
          this.playerHp -= e.damage;
          this.invulnT = 0.8;
          this._updateHpBar();
          this._combatToast(`ouch! -${e.damage} HP`);
          this.bread.drawFace('o');
          if (this.playerHp <= 0) this._defeat();
        }
      }
      if (e.state !== 'attacking') e.dealtDamage = false;
    });

    if (this.combatToastT > 0){
      this.combatToastT -= dt;
      if (this.combatToastT <= 0 && this.toastEl) this.toastEl.style.opacity = '0';
    }

    this.bread.update(dt);

    if (this.state === 'explore'){
      let near = null, nd = Infinity;
      this.hotspots.forEach(h => {
        if (h.label.includes('chest') && this.enemies.some(e => !e.dead)) return;
        const d = h.pos.distanceTo(playerPos);
        if (d < nd){ nd = d; near = h; }
      });
      if (near && nd < near.r){
        if (this.nearSpot !== near){
          this.nearSpot = near;
          HUD.showEnter(near.label, () => near.action());
        }
      } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }
    }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    const b = this.roomBounds;
    this.camTarget.lerp(new THREE.Vector3(playerPos.x * 0.5, 1.6, playerPos.z * 0.5 + 1), 0.06);
    this.camTarget.z = Math.max(b.z0 + 2, Math.min(b.z1 - 2, this.camTarget.z));
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this._clampCamera();
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
